import { x25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { CRYPTO_KID, getHandshakePathname, getServerPubFingerprint, HKDF_INFO } from "./config";
import { b64uDecode, b64uEncode, concatBytes } from "./utils";

export interface HandshakeSession {
  sid: string;
  sessionKey: Uint8Array;
  expireAt: number;
}

type HandshakeResponse = {
  status_code: number;
  error_code?: string;
  data?: {
    sid: string;
    server_pub: string;
    expire: number;
    server_ts: number;
  };
};

export async function doHandshake(apiOrigin: string): Promise<HandshakeSession> {
  const expectedFp = getServerPubFingerprint();
  if (!expectedFp && !import.meta.env.DEV) {
    throw new Error("missing VITE_CRYPTO_SERVER_PUB_FINGERPRINT");
  }

  const clientPriv = x25519.utils.randomSecretKey();
  const clientPub = x25519.getPublicKey(clientPriv);

  const nonce = crypto.getRandomValues(new Uint8Array(16));
  const reqBody = JSON.stringify({
    client_pub: b64uEncode(clientPub),
    ts: Math.floor(Date.now() / 1000),
    nonce: b64uEncode(nonce),
    kid: CRYPTO_KID,
  });

  const handshakeUrl = `${apiOrigin}${getHandshakePathname()}`;
  const resp = await fetch(handshakeUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: reqBody,
  });

  const data = (await resp.json()) as HandshakeResponse;
  if (data.status_code !== 200 || !data.data) {
    throw new Error(`handshake failed: ${data.status_code} ${data.error_code || ""}`.trim());
  }

  const serverPubBytes = b64uDecode(data.data.server_pub);
  if (expectedFp) {
    const gotFp = b64uEncode(sha256(serverPubBytes));
    if (gotFp !== expectedFp) {
      throw new Error(`MITM detected: server_pub fingerprint mismatch (got ${gotFp})`);
    }
  } else {
    console.warn("[crypto] VITE_CRYPTO_SERVER_PUB_FINGERPRINT not set; fingerprint check skipped in DEV");
  }

  const shared = x25519.getSharedSecret(clientPriv, serverPubBytes);
  const salt = concatBytes(clientPub, serverPubBytes);
  const sessionKey = hkdf(sha256, shared, salt, HKDF_INFO, 32);

  return {
    sid: data.data.sid,
    sessionKey,
    expireAt: Date.now() + data.data.expire * 1000,
  };
}
